import { useEffect, useState, type SetStateAction } from "react";
import {
  ProjectPageService,
  type PageResponse,
  type ProjectJobsPageQuery,
  type ProjectJobsSummary,
  type ProjectBranchRegionOptions,
  type ProjectOpportunitiesPageQuery,
  type ProjectOpportunitiesSummary,
  type ProjectReadinessPageQuery,
  type ProjectReadinessRow,
  type ProjectReadinessSummary,
} from "@/services/project-page.service";
import type { Job } from "@/data/types";
import type { Opportunity } from "@/data/opportunityTypes";

function emptyPage<T>(): PageResponse<T> {
  return { data: [], count: 0, error: null };
}

export function useProjectJobsPage(query: ProjectJobsPageQuery & { enabled?: boolean }) {
  const [state, setState] = useState<PageResponse<Job>>(emptyPage<Job>());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!query.enabled) {
      setState(emptyPage<Job>());
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }
    setLoading(true);
    void ProjectPageService.getJobsPage(query).then((next) => {
      if (cancelled) return;
      setState(next);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [query.enabled, query.tab, query.page, query.pageSize, query.search, query.branch, query.region]);

  return { jobs: state.data, count: state.count, error: state.error, loading, setJobs: (updater: SetStateAction<Job[]>) => {
    setState((current) => ({
      ...current,
      data: typeof updater === "function" ? (updater as (prev: Job[]) => Job[])(current.data) : updater,
    }));
  } };
}

export function useProjectJobsSummary(filters: { enabled?: boolean; branch?: string; region?: string }) {
  const [data, setData] = useState<ProjectJobsSummary | null>(null);
  const [error, setError] = useState<unknown | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!filters.enabled) {
      setData(null);
      setError(null);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }
    setLoading(true);
    void ProjectPageService.getJobsSummary(filters).then((result) => {
      if (cancelled) return;
      setData(result.data);
      setError(result.error);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [filters.enabled, filters.branch, filters.region]);

  return { data, error, loading };
}

export function useProjectJobBranchRegionOptions(filters: { enabled?: boolean }) {
  const [data, setData] = useState<ProjectBranchRegionOptions | null>(null);
  const [error, setError] = useState<unknown | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!filters.enabled) {
      setData(null);
      setError(null);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }
    setLoading(true);
    void ProjectPageService.getJobBranchRegionOptions().then((result) => {
      if (cancelled) return;
      setData(result.data);
      setError(result.error);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [filters.enabled]);

  return { data, error, loading };
}

export function useProjectOpportunitiesPage(query: ProjectOpportunitiesPageQuery & { enabled?: boolean }) {
  const [state, setState] = useState<PageResponse<Opportunity>>(emptyPage<Opportunity>());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!query.enabled) {
      setState(emptyPage<Opportunity>());
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }
    setLoading(true);
    void ProjectPageService.getOpportunitiesPage(query).then((next) => {
      if (cancelled) return;
      setState(next);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [query.enabled, query.tab, query.page, query.pageSize, query.search, query.status, query.rep, query.disposition, query.dateRange]);

  return { opportunities: state.data, count: state.count, error: state.error, loading };
}

export function useProjectOpportunitiesSummary(filters: { enabled?: boolean; rep?: string }) {
  const [data, setData] = useState<ProjectOpportunitiesSummary | null>(null);
  const [error, setError] = useState<unknown | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!filters.enabled) {
      setData(null);
      setError(null);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }
    setLoading(true);
    void ProjectPageService.getOpportunitiesSummary(filters).then((result) => {
      if (cancelled) return;
      setData(result.data);
      setError(result.error);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [filters.enabled, filters.rep]);

  return { data, error, loading };
}

export function useProjectReadinessPage(query: ProjectReadinessPageQuery & { enabled?: boolean }) {
  const [state, setState] = useState<PageResponse<ProjectReadinessRow>>(emptyPage<ProjectReadinessRow>());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!query.enabled) {
      setState(emptyPage<ProjectReadinessRow>());
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }
    setLoading(true);
    void ProjectPageService.getReadinessPage(query).then((next) => {
      if (cancelled) return;
      setState(next);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [query.enabled, query.lane, query.page, query.pageSize, query.search]);

  return { rows: state.data, count: state.count, error: state.error, loading };
}

export function useProjectReadinessSummary(filters: { enabled?: boolean }) {
  const [data, setData] = useState<ProjectReadinessSummary | null>(null);
  const [error, setError] = useState<unknown | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!filters.enabled) {
      setData(null);
      setError(null);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }
    setLoading(true);
    void ProjectPageService.getReadinessSummary().then((result) => {
      if (cancelled) return;
      setData(result.data);
      setError(result.error);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [filters.enabled]);

  return { data, error, loading };
}
